import { wallOpeningObb, wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import { windowIntersectsCutPlane } from "building-generation/elements/window/windowQuery";
import { WINDOW_FRAME_DEPTH } from "building-generation/elements/window/windowUtils";
import type { GridWindowMullionSettings } from "building-types/settings/windowMullionSettings";
import {
  WindowOperationType,
  WindowPanelType,
  WindowShapeTypes,
  WindowVertDivisions,
} from "building-types/settings/windowTypesEnum";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType, sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import type { MergedWindowSettings, GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { DEFAULT_INTEGRAL_MULLION_WIDTH } from "common/constants/buildingObjects";
import { ensureDefined } from "common/utils/assertOfType";
import { windowPlacementsOnLevel } from "doc/building/windowPlacementsOnLevel";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import type { PlanCollider } from "doc/plans/interiorLabelObstacle";
import { CollisionType } from "doc/plans/interiorLabelObstacle";
import type { PlanGenerationContext, PlanGenerationContextBase } from "doc/plans/planGeometrySource";
import type { TaggedPlanGeometry } from "doc/plans/planGeometrySourceTypes";
import { makePlanColliderFromSheetGeometry } from "doc/plans/sheetGeometryCollision";
import { angleClean } from "geom/angle";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { line2Normal } from "geom/line2";
import type { OBB2 } from "geom/obb2";
import { polyLine2Thicken } from "geom/offset";
import { planeNormal } from "geom/plane";
import {
  polyLine2Copy,
  polyLine2EdgeLength,
  polyLine2Explode,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2Shift,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { rect2Extend, rect2MultiSplit, rect2MultiSplitFlex, rect2Split } from "geom/rect2";
import { vec2AngleBetween } from "geom/vec2";
import { windowsCrossLevelCutPlane, windowUnitsCrossLevelCutPlane } from "lib/house/building/windowsCrossLevelCutPlane";
import { windowUnitsOnLevel } from "lib/house/building/windowUnitsOnLevel";
import type { Label } from "lib/house/plans/label";
import { labelNew, tagFontSize } from "lib/house/plans/label";
import { getReportingUnitRoomIds, hostRoomsInReportingUnit } from "../reportingUnitRooms";
import { makeSheetGeometryFromLabels } from "./planLabels";
import { makeSheetGeometryForOpeningCutTrim, makeSheetGeometryForOpeningProjectedTrim } from "./planOpenings";
import { placeOpeningTags } from "./planTags";

type WindowPlanContext = PlanGenerationContextBase & Pick<PlanGenerationContext, "selectedReportingUnitLabelId">;

function windowUnitInReportingUnit(
  windowUnit: { windowPlacement: { frontRoom?: { id: string }; backRoom?: { id: string } } },
  reportingUnitRoomIds: ReadonlySet<string> | undefined,
): boolean {
  return hostRoomsInReportingUnit(reportingUnitRoomIds, [
    windowUnit.windowPlacement.frontRoom,
    windowUnit.windowPlacement.backRoom,
  ]);
}

export function makeSheetGeometryForWindowCutTrim({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: WindowPlanContext) {
  const result: SheetGeometry[] = [];
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);

  const windowUnits = [
    ...windowUnitsOnLevel(levelId, building.windowUnits).filter((u) =>
      windowIntersectsCutPlane(u.windowPlacement, cutPlaneHeight, building.roomFloorAssemblies),
    ),
    ...windowUnitsCrossLevelCutPlane(
      building.levels,
      levelId,
      building.windowUnits,
      cutPlaneHeight,
      building.planarGraphLevelAssociations,
    ),
  ];

  for (const windowUnit of windowUnits) {
    if (!windowUnitInReportingUnit(windowUnit, reportingUnitRoomIds)) {
      continue;
    }
    if (windowUnit.windowPlacement.handle && windowUnit.windowPlacement.opening) {
      result.push(
        ...makeSheetGeometryForOpeningCutTrim(
          windowUnit.windowPlacement.handle,
          ensureDefined(windowUnit.windowPlacement.opening),
        ),
      );
    }
  }

  return result;
}

export function makeSheetGeometryForWindowProjectedTrim({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: WindowPlanContext) {
  const result: SheetGeometry[] = [];
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);

  const windowUnits = [
    ...windowUnitsOnLevel(levelId, building.windowUnits).filter((u) =>
      windowIntersectsCutPlane(u.windowPlacement, cutPlaneHeight, building.roomFloorAssemblies),
    ),
    ...windowUnitsCrossLevelCutPlane(
      building.levels,
      levelId,
      building.windowUnits,
      cutPlaneHeight,
      building.planarGraphLevelAssociations,
    ),
  ];

  for (const windowUnit of windowUnits) {
    if (!windowUnitInReportingUnit(windowUnit, reportingUnitRoomIds)) {
      continue;
    }
    if (windowUnit.windowPlacement.handle && windowUnit.windowPlacement.opening) {
      result.push(
        ...makeSheetGeometryForOpeningProjectedTrim(
          windowUnit.windowPlacement.handle,
          ensureDefined(windowUnit.windowPlacement.opening),
        ),
      );
    }
  }

  return result;
}

export function makeSheetGeometryForWindows({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: WindowPlanContext) {
  const result: SheetGeometry[] = [];
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);

  const windowUnits = [
    ...windowUnitsOnLevel(levelId, building.windowUnits).filter((u) =>
      windowIntersectsCutPlane(u.windowPlacement, cutPlaneHeight, building.roomFloorAssemblies),
    ),
    ...windowUnitsCrossLevelCutPlane(
      building.levels,
      levelId,
      building.windowUnits,
      cutPlaneHeight,
      building.planarGraphLevelAssociations,
    ),
  ];

  for (const windowUnit of windowUnits) {
    if (!windowUnitInReportingUnit(windowUnit, reportingUnitRoomIds)) {
      continue;
    }
    const windowHandle = windowUnit.windowPlacement.handle ?? windowUnit.windowPlacement.frontRoom?.roomHandle;

    const linework: PolyLine2[] = [];
    const relaxationObstacleOBBs: OBB2[] = [];

    const mergedWindowSettings = getMergedWindowSettings({
      window: windowUnit.windowPlacement,
      descriptor: windowUnit.windowPlacement.descriptor,
      homeSettings: building.settings,
      entitySettings: building.doc.entitySettings,
      activeSettingsSets: building.settingsSets,
      optionChoices: building.optionChoices,
      settings: building.doc.settings,
    });

    makeWindowPlanLinework(
      windowUnit.windowPlacement,
      linework,
      relaxationObstacleOBBs,
      windowUnit.depthAlignmentOffset,
      mergedWindowSettings,
    );

    result.push(
      sheetPolylineGeometryNew({
        handle: windowHandle,
        paths: linework,
        relaxationObstacleOBBs,
      }),
    );
  }

  return result;
}

export function makeWindowPlanLinework(
  window: GeneratedWindowPlacement,
  linework: PolyLine2[],
  obbs: OBB2[],
  depthAlignmentOffset: number,
  mergedSettings?: MergedWindowSettings,
  tolerance = DIST_EPSILON,
) {
  const { opening, descriptor } = window;
  if (!opening) {
    return;
  }

  if (descriptor.open) {
    return;
  }

  const panelType = window.materials?.panelType;
  if (panelType === WindowPanelType.FAUX_SHUTTER) {
    return;
  }

  let verticalDivisionSpacing = DEFAULT_INTEGRAL_MULLION_WIDTH;

  if (mergedSettings) {
    verticalDivisionSpacing = ensureDefined(mergedSettings.verticalDivisionSpacing);
  }

  const obb = wallOpeningObb(opening);
  if (obb) {
    obbs.push(obb);
  }

  const outline1 = window.reverse
    ? polyLine2Copy(wallOpeningPlanExtents(opening))
    : polyLine2Shift(polyLine2Copy(wallOpeningPlanExtents(opening)), 2);
  linework.push(outline1);

  const exteriorPlane = window.reverse ? opening.frontPlane : opening.backPlane;
  const [nx, ny] = planeNormal(exteriorPlane);

  const exteriorEdge = polyLine2New([outline1[0], outline1[1], outline1[2], outline1[3]]);

  // ensure the exterior edge of the planExtents is aligned with the exterior
  // plane of the wall
  const [ex, ey] = line2Normal(exteriorEdge[0], exteriorEdge[1], exteriorEdge[2], exteriorEdge[3]);
  if (Math.abs(angleClean(vec2AngleBetween(ex, ey, nx, ny))) > 0.1) {
    polyLine2Reverse(exteriorEdge);
  }

  linework.push(outline1);

  polyLine2Move(exteriorEdge, nx * -depthAlignmentOffset, ny * -depthAlignmentOffset);
  const windowFrameOutline = polyLine2Thicken(exteriorEdge, -WINDOW_FRAME_DEPTH);
  linework.push(windowFrameOutline);

  const outline = polyLine2Copy(windowFrameOutline);
  let outlines = [outline];

  if (descriptor.shape === WindowShapeTypes.RECTANGLE || descriptor.shape === WindowShapeTypes.RECTANGLE_ARCHED_TOP) {
    if (descriptor.verticalDivisions === WindowVertDivisions.SINGLE) {
      outlines = [outline];
    } else if (descriptor.verticalDivisions === WindowVertDivisions.TWIN) {
      outlines = rect2MultiSplitFlex(outline, [0, verticalDivisionSpacing, 0], true);
    } else if (descriptor.verticalDivisions === WindowVertDivisions.TRIPLE) {
      outlines = rect2MultiSplitFlex(outline, [0, verticalDivisionSpacing, 0, verticalDivisionSpacing, 0], true);
    }
  }

  for (const outline of outlines) {
    if (Math.abs(polyLine2EdgeLength(outline, 0) - verticalDivisionSpacing) > tolerance) {
      rect2Extend(outline, 1, 1);
      rect2Extend(outline, 3, 1);
      linework.push(...polyLine2Explode(outline));
      rect2Extend(outline, 0, 1);
      rect2Extend(outline, 2, 1);
      linework.push(...polyLine2Explode(outline));
      rect2Extend(outline, 1, 1);
      rect2Extend(outline, 3, 1);
      linework.push(...polyLine2Explode(outline));
    }
  }
}

/**
 * @NOTE - This function should be deprecated. It is here to maintain a legacy window feature
 * by simulating the existence of walls between vertical divisions
 * @deprecated
 */
export function makeSheetGeometryForWindowWallCrossSections({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: WindowPlanContext) {
  const result: SheetGeometry[] = [];
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);

  const windowUnits = [
    ...windowUnitsOnLevel(levelId, building.windowUnits).filter((u) =>
      windowIntersectsCutPlane(u.windowPlacement, cutPlaneHeight, building.roomFloorAssemblies),
    ),
    ...windowUnitsCrossLevelCutPlane(
      building.levels,
      levelId,
      building.windowUnits,
      cutPlaneHeight,
      building.planarGraphLevelAssociations,
    ),
  ];

  for (const windowUnit of windowUnits) {
    if (!windowUnitInReportingUnit(windowUnit, reportingUnitRoomIds)) {
      continue;
    }
    const windowHandle = windowUnit.windowPlacement.handle ?? windowUnit.windowPlacement.frontRoom?.docEntityHandle;

    const mergedWindowSettings = getMergedWindowSettings({
      window: windowUnit.windowPlacement,
      descriptor: windowUnit.windowPlacement.descriptor,
      homeSettings: building.settings,
      entitySettings: building.doc.entitySettings,
      activeSettingsSets: building.settingsSets,
      optionChoices: building.optionChoices,
      settings: building.doc.settings,
    });

    const crossSections = makeWindowPlanWallCrossSections(
      windowUnit.windowPlacement,
      windowUnit.depthAlignmentOffset,
      mergedWindowSettings,
    );

    result.push(
      sheetPolylineGeometryNew({
        handle: windowHandle,
        paths: crossSections,
      }),
    );
  }

  return result;
}

/**
 * @NOTE - This function should be deprecated. It is here to maintain a legacy window feature
 * by simulating the existence of walls between vertical divisions
 */
function makeWindowPlanWallCrossSections(
  window: GeneratedWindowPlacement,
  depthAlignmentOffset: number,
  mergedSettings?: MergedWindowSettings,
): PolyLine2[] {
  const wallCrossSections: PolyLine2[] = [];
  const { opening, descriptor } = window;
  if (!opening) {
    return wallCrossSections;
  }

  const panelType = window.materials?.panelType;
  if (panelType === WindowPanelType.FAUX_SHUTTER) {
    return [];
  }

  let factoryMulled = false;
  let verticalDivisionSpacing = DEFAULT_INTEGRAL_MULLION_WIDTH;

  if (mergedSettings) {
    const { exteriorCasing } = mergedSettings;

    if (!!exteriorCasing?.factoryMulled) {
      factoryMulled = exteriorCasing.factoryMulled;
    }

    if (!!mergedSettings.verticalDivisionSpacing) {
      verticalDivisionSpacing = mergedSettings.verticalDivisionSpacing;
    }
  }

  if (factoryMulled) {
    return wallCrossSections;
  }

  const outline1 = window.reverse
    ? polyLine2Copy(wallOpeningPlanExtents(opening))
    : polyLine2Shift(polyLine2Copy(wallOpeningPlanExtents(opening)), 2);
  const exteriorPlane = window.reverse ? opening.frontPlane : opening.backPlane;
  const [nx, ny] = planeNormal(exteriorPlane);

  if (polyLine2WindingDir(outline1) !== WindingDir.CCW) {
    polyLine2Shift(polyLine2Reverse(outline1), 1);
  }

  const exteriorEdge = polyLine2New([outline1[0], outline1[1], outline1[2], outline1[3]]);

  polyLine2Move(exteriorEdge, nx * -depthAlignmentOffset, ny * -depthAlignmentOffset);
  const windowFrameOutline = polyLine2Thicken(exteriorEdge, -WINDOW_FRAME_DEPTH);

  const outline = polyLine2Copy(windowFrameOutline);
  let outlines = [outline];

  if (descriptor.shape === WindowShapeTypes.RECTANGLE || descriptor.shape === WindowShapeTypes.RECTANGLE_ARCHED_TOP) {
    if (descriptor.verticalDivisions === WindowVertDivisions.SINGLE) {
      outlines = [outline];
    } else if (descriptor.verticalDivisions === WindowVertDivisions.TWIN) {
      outlines = rect2MultiSplitFlex(outline, [0, verticalDivisionSpacing, 0], true);

      if (!factoryMulled) {
        wallCrossSections.push(outlines[1]);
      }
    } else if (descriptor.verticalDivisions === WindowVertDivisions.TRIPLE) {
      outlines = rect2MultiSplitFlex(outline, [0, verticalDivisionSpacing, 0, verticalDivisionSpacing, 0], true);

      if (!factoryMulled) {
        wallCrossSections.push(outlines[1], outlines[3]);
      }
    }
  }

  return wallCrossSections;
}

export function makeSheetGeometryFromWindowTagLabels(props: PlanGenerationContext) {
  const { building, levelId, noSettings, cutPlaneHeight, selectedReportingUnitLabelId } = props;
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);
  const windows = [
    ...windowPlacementsOnLevel(levelId, building.windowPlacements).filter((w) =>
      windowIntersectsCutPlane(w, cutPlaneHeight, building.roomFloorAssemblies),
    ),
    ...windowsCrossLevelCutPlane(
      building.levels,
      levelId,
      building.windowPlacements,
      cutPlaneHeight,
      building.planarGraphLevelAssociations,
    ),
  ].filter((w) => hostRoomsInReportingUnit(reportingUnitRoomIds, [w.frontRoom, w.backRoom]));

  const { planset, windowMullions } = building.settings;
  const { paperSize } = planset;
  const fontSize = tagFontSize(paperSize);

  const windowTags: SheetGeometry[] = [];

  for (const window of windows) {
    if (!window.opening) {
      continue;
    }

    const planOutline = wallOpeningPlanExtents(window.opening);

    const { verticalDivisions, reportedName } = window.descriptor;
    const outline = window.reverse ? polyLine2Shift(polyLine2Copy(planOutline), 2) : polyLine2Copy(planOutline);

    const windowPlans: Float64Array[] = [];

    const { exteriorCasing } = getMergedWindowSettings({
      window,
      descriptor: window.descriptor,
      homeSettings: building.settings,
      entitySettings: building.doc.entitySettings,
      activeSettingsSets: building.settingsSets,
      optionChoices: building.optionChoices,
      settings: building.doc.settings,
    });

    if (!exteriorCasing?.factoryMulled) {
      if (verticalDivisions === WindowVertDivisions.TWIN) {
        windowPlans.push(...rect2Split(outline, true, 0.5, true));
      }

      if (verticalDivisions === WindowVertDivisions.TRIPLE) {
        windowPlans.push(...rect2MultiSplit(outline, true, [0.333, 0.666], true));
      }

      if (verticalDivisions === WindowVertDivisions.SINGLE) {
        windowPlans.push(outline);
      }
    } else {
      windowPlans.push(outline);
    }

    for (const planOutline of windowPlans) {
      // new opening with updated planOutline
      const opening = { ...window.opening, planOutline };
      let tagLabel = reportedName;

      if (planset.includeWindowTagExtensions) {
        const mullionSettings = windowMullions as GridWindowMullionSettings;
        const oprType = window.operationType ?? window.descriptor.operationType ?? mullionSettings.operationType;
        let oprExt = "";
        switch (oprType) {
          case WindowOperationType.DOUBLE_HUNG:
            oprExt = "DH";
            break;
          case WindowOperationType.SINGLE_HUNG:
            oprExt = "SH";
            break;
          case WindowOperationType.FIXED:
            oprExt = "FX";
            break;
          case WindowOperationType.SLIDING:
            oprExt = "SL";
            break;
          case WindowOperationType.AWNING:
            oprExt = "AW";
            break;
          case WindowOperationType.CASEMENT:
            oprExt = "CW";
            break;
          case undefined:
        }
        tagLabel += ` ${oprExt}`;
      }

      const windowReportedName = window.reportedName ?? tagLabel;
      const tagDetailLabel = window.tagDetails ?? window.descriptor.tagDetails;

      const windowLabels: Label[] = [];

      placeOpeningTags(
        opening,
        windowReportedName,
        tagDetailLabel,
        planset,
        window,
        (text, centerX, centerY, nX, nY, textRotation) => {
          windowLabels.push(
            labelNew({
              text,
              fontSize,
              centerX,
              centerY,
              textRotationRads: textRotation,
              relaxationNormalX: nX,
              relaxationNormalY: nY,
            }),
          );
        },
      );

      windowTags.push(
        ...makeSheetGeometryFromLabels({
          labels: windowLabels,
          handle: window.handle ?? building.levels[levelId].handle,
          noSettings,
        }),
      );
    }
  }

  return windowTags;
}

export function windowTagPlanColliders(geometry: readonly TaggedPlanGeometry[]): PlanCollider[] {
  return geometry.reduce((acc, g) => {
    if (g.type === SheetGeometryType.TEXT) {
      acc.push(
        makePlanColliderFromSheetGeometry({
          collisionType: CollisionType.OpeningTag,
          sheetTextGeomSource: g,
        }),
      );
    }
    return acc;
  }, [] as PlanCollider[]);
}
